import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultDashboard } from '../../utils/roleAccess';

const Breadcrumb = ({ items }) => {
  const location = useLocation();
  const { role } = useAuth();
  const homePath = getDefaultDashboard(role);

  // If explicit items are provided, render them
  if (items && items?.length > 0) {
    return (
      <nav aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm font-caption flex-wrap">
          {items?.map((item, index) => {
            const isLast = index === items?.length - 1;
            const href = item?.href || item?.path;
            return (
              <React.Fragment key={index}>
                {index > 0 && (
                  <li>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </li>
                )}
                <li>
                  {isLast || !href ? (
                    <span className="text-foreground font-medium" aria-current={isLast ? 'page' : undefined}>
                      {index === 0 && <Icon name="Home" size={14} className="inline mr-1 -mt-0.5" />}
                      {item?.label}
                    </span>
                  ) : (
                    <Link
                      to={href}
                      className="flex items-center text-muted-foreground hover:text-foreground transition-smooth"
                    >
                      {index === 0 && <Icon name="Home" size={14} className="mr-1" />}
                      {item?.label}
                    </Link>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    );
  }

  // Auto-generate from URL path
  const pathnames = location?.pathname?.split('/')?.filter((x) => x);

  const breadcrumbMap = {
    'admin-dashboard': 'Dashboard',
    'site-manager-dashboard': 'Dashboard',
    'worker-today': 'Today',
    'client-dashboard': 'Dashboard',
    'site-dashboard': 'Site Dashboard',
    'site-setup-wizard': 'Site Setup',
    'document-management': 'Documents',
    'company-registration': 'Registration',
    'login': 'Login',
  };

  const getBreadcrumbLabel = (path) => {
    return breadcrumbMap?.[path] || path?.charAt(0)?.toUpperCase() + path?.slice(1);
  };

  if (pathnames?.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className="py-4">
      <ol className="flex items-center gap-2 text-sm font-caption">
        <li>
          <Link
            to={homePath}
            className="flex items-center text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="Home" size={16} className="mr-1" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
        </li>

        {pathnames?.map((path, index) => {
          const routeTo = `/${pathnames?.slice(0, index + 1)?.join('/')}`;
          const isLast = index === pathnames?.length - 1;
          const label = getBreadcrumbLabel(path);

          return (
            <React.Fragment key={routeTo}>
              <li>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </li>
              <li>
                {isLast ? (
                  <span className="text-foreground font-medium" aria-current="page">
                    {label}
                  </span>
                ) : (
                  <Link
                    to={routeTo}
                    className="text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    {label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;